import React, { useEffect } from "react";
import { IonContent, IonButton, IonIcon } from "@ionic/react";
import { chevronBack } from "ionicons/icons";
import "./HealthConnect.css";
import { AppleHealthIcon, GoogleHealthIcon } from "../../../assets/icons";
import { Preferences } from "@capacitor/preferences";
import { updateUserPreferences } from "../../../services/user";
import useAuth from "../../../hooks/useAuth";

import { HealthPreferencesStorage } from "../../../pages/settings/MyHealthData";
import {
  READ_PERMISSIONS_ANDROID,
  READ_PERMISSIONS_IOS,
} from "../../../utils/constants";
import {
  requestHealthKitPermissions,
  ensureHealthServiceAvailable,
} from "../../../services/healthkit";
import analytics from "../../../services/firebaseAnalytics";

interface HealthConnectProps {
  handleContinue: () => void;
  prevStep: () => void;
  nextStep: () => void;
  skipStep: () => void;
  currentStep: number;
  steps: any[];
  platform: "ios" | "android" | "web";
}

const HealthConnect: React.FC<HealthConnectProps> = ({
  prevStep,
  handleContinue,
  nextStep,
  skipStep,
  currentStep,
  steps,
  platform,
}) => {
  const { user } = useAuth();

  useEffect(() => {
    const logAnalyticsEvent = async () => {
      await analytics.logEvent("onboarding_HealthService_viewed", {
        platform: platform,
      });
    };
    logAnalyticsEvent();
  }, [platform]);

  console.log("Platform selected:", platform);

  // Select the appropriate icon component based on platform
  const HealthIcon =
    platform === "ios"
      ? AppleHealthIcon
      : platform === "android"
      ? GoogleHealthIcon
      : null;

  const platformName =
    platform === "ios"
      ? "Apple Health"
      : platform === "android"
      ? "Health Connect"
      : null;

  const isClicked = async () => {
    const isIOS = platform === "ios";
    const isAndroid = platform === "android";

    if (isIOS || isAndroid) {
      // Check if health service is available, redirect to Play Store if needed (Android)
      const availabilityResult = await ensureHealthServiceAvailable(
        platform as "ios" | "android"
      );

      if (!availabilityResult.available) {
        // Not available - user redirected to Play Store (Android) or received error (iOS)
        // When they come back and click "Continue" again, this will recheck automatically
        console.log("Health service not available:", availabilityResult.message);
        return; // Don't proceed, let user install and try again
      }

      // Health service is available - proceed with permissions
      const prefPayload = {
        health_connect: true,
        track_health_data: {
          apple: isIOS,
          google: isAndroid,
          step_tracking: true,
          sleep_tracking: true,
          heart_rate: true,
        },
      };

      const selectedTracks =
        platform === "ios"
          ? [...READ_PERMISSIONS_IOS]
          : [...READ_PERMISSIONS_ANDROID];
      try {
        await Preferences.set({ key: "accessHealthKit", value: "true" });
        await updateUserPreferences(prefPayload);
        await HealthPreferencesStorage.savePreferences(
          isIOS ? "apple" : "google",
          selectedTracks
        );
        await requestHealthKitPermissions(selectedTracks);
        handleContinue();
      } catch (error) {
        console.error("Error updating preferences and creating goals:", error);
      }
    } else {
      // Web platform - skip this step
      nextStep();
      console.log("Health platform not available or not supported");
    }
  };

  return (
    <IonContent className="ion-padding onboarding-section ">
      <div className="navigation-header">
        <button className="back-text-button" onClick={prevStep}>
          <IonIcon icon={chevronBack} className="back-icon" />
          <span>Back</span>
        </button>
      </div>

      <div
        className={`health-connect-container ${
          platform === "android" ? "android" : ""
        }`}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        {/* Health Icon (not shown for web) */}
        {HealthIcon && (
          <div className="health-icon-container">
            <HealthIcon className="health-heart" aria-hidden="true" />
          </div>
        )}

        {/* Title / Description */}
        {platform === "web" ? (
          <>
            <h1 className="health-title">Not available on the web</h1>
            <p className="health-description">
              Connecting to health platforms is not available from a web
              browser. Please download the app to connect health data and use
              this feature.
            </p>
          </>
        ) : (
          <>
            <h1 className="health-title">
              Connect to
              <br />
              {platformName}
            </h1>
            <p className="health-description">
              By connecting with {platformName}, you can see other health data
              alongside BiWaze® treatments. See patterns and connect the dots!
            </p>
          </>
        )}
      </div>

      {/* Progress Indicator */}
      <div className="progress-indicator">
        {steps.map((_, index) => (
          <div
            key={index}
            className={`progress-dot ${currentStep === index ? "active" : ""}`}
          />
        ))}
      </div>

      {/* Connect Button */}
      <div className="button-stack-container">
        <IonButton
          expand="block"
          className="primary-button"
          onClick={isClicked}
        >
          Continue
        </IonButton>
        {/* <button className="skip-link" onClick={nextStep}>
          Skip for now
        </button> */}
      </div>
    </IonContent>
  );
};

export default HealthConnect;
